<template>
  <div>
    <div class="modal-card" style="width: 900px; height: auto">
      <form @submit.prevent="saveAction">
        <section class="modal-card-body">
          <b-notification :closable="false">
            <div class="columns">
              <div class="column is-6">
                <l-field
                  :label="$t('compTransitionActionsEdit.action-title')"
                  :tooltip="
                    $t('compTransitionActionsEdit.tooltip-action-title')
                  "
                  tooltip-position="is-bottom"
                  :type="{ 'is-danger': errors.has('title') }"
                  :message="errors.first('title')"
                >
                  <b-input
                    v-model="action.title"
                    required
                    expanded
                    icon="tags"
                    name="title"
                    :key="`title-${action.id}`"
                    v-validate="'required|min:10|max:100'"
                  ></b-input>
                </l-field>
              </div>
              <div class="column">
                <l-field
                  :label="$t('compTransitionActionsEdit.action-start')"
                  :tooltip="
                    $t('compTransitionActionsEdit.tooltip-action-start')
                  "
                  tooltip-position="is-bottom"
                  :type="{ 'is-danger': errors.has('actionStart') }"
                  :message="errors.first('actionStart')"
                >
                  <b-datepicker
                    v-model="action.start"
                    :placeholder="$t('compTransitionActionsEdit.action-start')"
                    icon="calendar"
                    name="actionStart"
                    v-validate="'required'"
                    :append-to-body="true"
                    editable
                    ref="afterTarget"
                  />
                </l-field>
              </div>
              <div class="column">
                <l-field
                  :label="$t('compTransitionActionsEdit.action-end')"
                  :tooltip="$t('compTransitionActionsEdit.tooltip-action-end')"
                  tooltip-position="is-bottom"
                  :type="{ 'is-danger': errors.has('actionEnd') }"
                  :message="
                    errors.first('actionEnd')
                      ? $t('compTransitionActionsEdit.end-after-start')
                      : ''
                  "
                >
                  <b-datepicker
                    v-model="action.end"
                    :placeholder="$t('compTransitionActionsEdit.action-end')"
                    v-validate="'after:afterTarget'"
                    name="actionEnd"
                    icon="calendar"
                    :append-to-body="true"
                    editable
                  />
                </l-field>
              </div>
            </div>
            <b-field>
              <b-switch v-model="action.abandoned">
                {{ $t('compTransitionActionsEdit.action-abandoned') }}
              </b-switch>
            </b-field>
          </b-notification>

          <b-notification :closable="false">
            <l-field
              :label="$t('compTransitionActionsEdit.action-contact')"
              :tooltip="$t('compTransitionActionsEdit.action-contact-tooltip')"
              :message="errors.first('contact')"
            >
              <b-input
                v-model="action.contact"
                expanded
                icon="tags"
                name="contact"
                :placeholder="action.displayContact()"
                :key="`contact-${action.id}`"
                v-validate="'email'"
              >
              </b-input>
            </l-field>
          </b-notification>

          <b-notification :closable="false">
            <l-field
              :label="$t('compTransitionActionsEdit.action-tags')"
              :tooltip="$t('compTransitionActionsEdit.action-tags-tooltip')"
            >
              <b-taginput
                ellipsis
                :allow-duplicate="false"
                v-model="action.tags"
                :data="filteredTags"
                autocomplete
                @typing="getFilteredTags"
                :allow-new="false"
                :open-on-focus="true"
                :icon="'caret-right'"
                :placeholder="$t('add')"
                ref="taginput"
              >
                <!-- suggested items -->
                <template slot-scope="props">
                  <action-tag
                    :tag="props.option"
                    :tooltipPosition="'is-right'"
                    :tooltipMultilined="true"
                  ></action-tag>
                </template>
                <!-- selected items -->
                <template #selected="props">
                  <action-tag
                    v-for="(tag, index) in props.tags"
                    :key="index"
                    :tag="tag"
                    closable
                    @close="$refs.taginput.removeTag(index, $event)"
                  >
                  </action-tag>
                </template>
              </b-taginput>
            </l-field>
          </b-notification>

          <b-notification :closable="false">
            <b-field
              :label="$t('compTransitionActionsEdit.action-text')"
              :type="{ 'is-danger': errors.has('content') }"
              :message="errors.first('content')"
            >
              <editor
                :key="`content-${action.id}`"
                v-model="action.text"
                data-vv-name="content"
                v-validate="'required|min:25|max:10000'"
              />
            </b-field>
          </b-notification>

          <l-field
            :label="$t('compTransitionActionsEdit.label-files')"
            :tooltip="$t('compTransitionActionsEdit.help-label-files')"
            :type="{ 'is-danger': errors.has('action.files') }"
            :message="
              $t('compTransitionActionsEdit.' + errors.first('action.files'))
            "
          >
            <upload
              v-bind:dropFiles.sync="dropFiles"
              :quota="quota"
              :maxQuota="maxQuota"
              :actionQuota="actionQuota"
              :withQuota="withQuota"
              data-vv-name="action.files"
              v-validate:dropFiles="`quota:${remainingQuota}`"
              @onDeleteActionFile="onDeleteActionFile"
            ></upload>
          </l-field>
        </section>

        <footer class="modal-card-foot buttons is-right">
          <nav class="level">
            <div class="level-left"></div>
            <div class="buttons level-right">
              <b-button
                class="button"
                icon-left="close"
                @click="$emit('onActionEditorCancel', action, dropFiles)"
              >
                {{ $t('cancel') }}
              </b-button>
              <button type="submit" active class="button is-primary">
                <span class="icon is-small"><i class="fa fa-plus"></i></span>
                <span v-if="action.id === null"> {{ $t('add') }} </span>
                <span v-else> {{ $t('update') }} </span>
              </button>
            </div>
          </nav>
        </footer>
      </form>
    </div>
  </div>
</template>

<script>
import { useCoreStore } from '@/stores/core.js'
import { Background } from '@/mixins/background.js'
import { TAGS, USER_STATUSES } from '@/models/transition/transition.js'

import _ from 'lodash'

export default {
  name: 'ActionEditor',
  props: {
    action: {
      type: Object,
      required: true
    },
    quota: {
      type: Number,
      required: true
    },
    maxQuota: {
      type: Number,
      required: true
    },
    actionQuota: {
      type: Number,
      required: true
    },

    withQuota: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  components: {
    editor: () => import('@/components/Editor.vue'),
    'action-tag': () => import('@/components/transition/ActionTag.vue'),
    upload: () => import('@/components/transition/Upload.vue'),
    'l-field': () => import('@/components/LField.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  mixins: [Background],
  emit: [
    'is-valid',
    'is-modified',
    'onActionEditorSave',
    'onActionEditorCancel',
    'onDeleteActionFile'
  ],
  data: function () {
    return {
      // List of attached files
      files: this.action.files,
      // The list of possible tags
      // It's used to make suggestions on the corresponding tag inputs
      availableTags: TAGS,
      filteredTags: TAGS,
      // The list of Statused
      // It's used to make suggestions on the corresponding tag inputs
      statuses: USER_STATUSES,
      // whether we have to display the creation/update modal
      displayModal: false,
      // Display the files to attach
      displayFiles: false
    }
  },
  computed: {
    mode () {
      if (this.action.id == null) {
        return 'creation'
      } else {
        return 'edition'
      }
    },
    modeTitle () {
      return `title-${this.mode}`
    },
    modeInfo () {
      return `info-${this.mode}`
    },
    disableUpload () {
      return this.maxQuota === 0
    },
    dropFiles: {
      get: function () {
        return this.files
      },
      set: function (newValue) {
        this.files = newValue
      }
    },
    /**
     * At any time the remaining quota is the diff between what we're allowed to
     * have and what we already have.
     */
    remainingQuota () {
      let initialFiles = this.files.filter((f) => !(f instanceof File))
      return Math.min(
        this.maxQuota - this.quota,
        this.actionQuota - _.sumBy(initialFiles, (f) => f.size)
      )
    }
  },
  methods: {
    getFilteredTags (text) {
      // support autocompletion for several lang
      this.filteredTags = this.availableTags.filter((tag) => tag.match(text))
    },
    saveAction () {
      let arg = { action: this.action, files: this.dropFiles }
      this.validateBgUi(async () => this.$emit('onActionEditorSave', arg))
    },
    onDeleteActionFile (file) {
      this.$emit('onDeleteActionFile', file)
    }
  },
  setup () {
    const coreStore = useCoreStore()
    return { coreStore }
  },
  mounted () {}
}
</script>

<style>
.clabel {
  color: #363636;
  font-size: 1rem;
  font-weight: 700;
  padding-right: 10px;
}
.tags .ctag {
  height: 3em;
}
.cnotification {
  padding: 0.5rem 2.5rem 0.5rem 1.5rem;
}
</style>
